// Мигание заголовка вкладки браузера при новом сообщении.
// Мигает только пока вкладка неактивна (свёрнута/открыта другая вкладка),
// и автоматически останавливается, когда пользователь возвращается на вкладку.
// Дополнительно: рисует красную точку поверх favicon на время уведомления.

const FLASH_MESSAGE = 'Новое сообщение'
const FLASH_INTERVAL_MS = 1000
const BADGE_COLOR = '#ff3b30'

const BADGE_LINK_ID = 'favicon-badge'

let intervalId: number | null = null
let baseTitle: string | null = null
let showingAlert = false
let listenerAttached = false
let faviconBadgeActive = false
let detachedIconLinks: HTMLLinkElement[] = []

// favicon, который браузер реально показывает.
// defineFavicon() (см. utils.js) оставляет в DOM ровно одну scheme-иконку под
// текущую тему ОС, поэтому ориентируемся на неё (и игнорируем наш бейдж-link).
function getDisplayedFaviconLink(): HTMLLinkElement | null {
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    const id = isDark ? 'dark-scheme-icon' : 'light-scheme-icon'
    return (
        (document.getElementById(id) as HTMLLinkElement | null) ??
        (document.querySelector(
            `link[rel~='icon']:not(#${BADGE_LINK_ID})`
        ) as HTMLLinkElement | null)
    )
}

// Рисует favicon с красной точкой в правом верхнем углу и отдаёт data-URL.
function drawBadgedFavicon(
    srcHref: string,
    onReady: (dataUrl: string) => void
): void {
    const img = new Image()
    img.onload = () => {
        const size = 32
        const canvas = document.createElement('canvas')
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        ctx.drawImage(img, 0, 0, size, size)
        const r = 8
        ctx.beginPath()
        ctx.arc(size - r - 1, r + 1, r, 0, 2 * Math.PI)
        ctx.fillStyle = BADGE_COLOR
        ctx.fill()
        onReady(canvas.toDataURL('image/png'))
    }
    img.onerror = () => {
        // eslint-disable-next-line no-console
        console.warn(
            '[tabNotification] не удалось загрузить favicon для бейджа',
            srcHref
        )
    }
    img.src = srcHref
}

// Ключевое отличие от прежней версии: НЕ мутируем href существующего <link>
// (Chrome это часто игнорирует и иконка в табе не обновляется), а добавляем
// отдельный <link rel="icon"> ПОСЛЕДНИМ — браузер гарантированно перерисовывает
// иконку. При снятии бейджа просто удаляем этот элемент, и возвращается исходная.
function showFaviconBadge(): void {
    if (faviconBadgeActive) return
    const baseLink = getDisplayedFaviconLink()
    if (!baseLink) return
    faviconBadgeActive = true
    // Chrome НЕ декодирует .ico в <img>/canvas (favicon рендерится, но Image()
    // падает в onerror) — поэтому рисуем поверх .png-копии того же favicon.
    // favicon.png / favicon-dark.png лежат в public/ рядом с .ico.
    const baseHref = baseLink.href.replace(/\.ico($|\?)/, '.png$1')
    drawBadgedFavicon(baseHref, (dataUrl) => {
        if (!faviconBadgeActive) return
        let badge = document.getElementById(
            BADGE_LINK_ID
        ) as HTMLLinkElement | null
        if (!badge) {
            badge = document.createElement('link')
            badge.id = BADGE_LINK_ID
            badge.rel = 'icon'
        }
        badge.href = dataUrl
        // Chrome продолжает показывать исходный <link rel="icon">, даже если
        // добавить новый — поэтому ОТКРЕПЛЯЕМ оригиналы (запоминаем, чтобы
        // вернуть при снятии бейджа) и добавляем бейдж. Проверено в реальном Chrome.
        detachedIconLinks = Array.from(
            document.querySelectorAll<HTMLLinkElement>(
                `link[rel~='icon']:not(#${BADGE_LINK_ID})`
            )
        )
        detachedIconLinks.forEach((el) => el.remove())
        document.head.appendChild(badge)
    })
}

function hideFaviconBadge(): void {
    if (!faviconBadgeActive) return
    faviconBadgeActive = false
    const badge = document.getElementById(BADGE_LINK_ID)
    if (badge) badge.remove()
    // Возвращаем оригинальные иконки — браузер снова показывает обычный favicon.
    detachedIconLinks.forEach((el) => document.head.appendChild(el))
    detachedIconLinks = []
}

function restoreTitle(): void {
    if (baseTitle !== null) {
        document.title = baseTitle
        baseTitle = null
    }
    showingAlert = false
}

function stop(): void {
    if (intervalId !== null) {
        window.clearInterval(intervalId)
        intervalId = null
    }
    restoreTitle()
    hideFaviconBadge()
}

function onVisibilityChange(): void {
    // Вернулись на вкладку — гасим мигание и возвращаем исходный заголовок и иконку
    if (!document.hidden) {
        stop()
    }
}

function ensureListener(): void {
    if (!listenerAttached) {
        document.addEventListener('visibilitychange', onVisibilityChange)
        listenerAttached = true
    }
}

function flashNewMessage(message: string = FLASH_MESSAGE): void {
    // Мигаем только когда вкладка в фоне — если пользователь и так смотрит, не трогаем заголовок
    if (!document.hidden) {
        return
    }
    ensureListener()
    // Уже мигает — не плодим интервалы, оставляем текущее мигание
    if (intervalId !== null) {
        return
    }

    showFaviconBadge()

    baseTitle = document.title
    // Сразу показываем алерт для мгновенной реакции, дальше — мигание
    showingAlert = true
    document.title = message
    intervalId = window.setInterval(() => {
        showingAlert = !showingAlert
        document.title = showingAlert ? message : (baseTitle as string)
    }, FLASH_INTERVAL_MS)
}

export const tabNotification = {
    flashNewMessage,
    stop,
}
